import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet, apiPatch, apiDelete, apiPost } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";
import { formatDateOnly } from "../utils/dateTime";
import { isValidEmail, normalizeEmail } from "../utils/email";
import AdminSubscribers from "./AdminSubscribers";
import Metrics from "./Metrics";
import HealthStatus from "../components/HealthStatus";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "SUSPENDED", label: "Suspended" },
];

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

export default function AdminUsers() {
  const { showError, showSuccess } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(() => {
    const raw = searchParams.get("tab");
    return ["users", "projects", "schedules", "subscribers", "metrics"].includes(raw) ? raw : "users";
  }, [searchParams]);

  const sectionTitle = useMemo(() => {
    switch (activeTab) {
      case "projects":
        return "Projects";
      case "schedules":
        return "Monitoring Schedules";
      case "subscribers":
        return "Subscribers";
      case "metrics":
        return "Telemetry Metrics";
      default:
        return "Users";
    }
  }, [activeTab]);

  const setActiveTab = (tab) => {
    setSearchParams(tab === "users" ? {} : { tab });
  };

  // --- Users Tab State ---
  const [usersRows, setUsersRows] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userUserid, setUserUserid] = useState("");
  const [userStatus, setUserStatus] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [usersPagination, setUsersPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [userDrafts, setUserDrafts] = useState({});
  const [savingUserId, setSavingUserId] = useState("");

  // --- Projects Tab State ---
  const [projectsRows, setProjectsRows] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectEmail, setProjectEmail] = useState("");
  const [projectUserid, setProjectUserid] = useState("");
  const [projectUrl, setProjectUrl] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const [projectsPagination, setProjectsPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [newProjEmail, setNewProjEmail] = useState("");
  const [newProjUrl, setNewProjUrl] = useState("");
  const [newProjLabel, setNewProjLabel] = useState("");
  const [addingProject, setAddingProject] = useState(false);

  // --- Schedules Tab State ---
  const [schedulesRows, setSchedulesRows] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [scheduleEmail, setScheduleEmail] = useState("");
  const [scheduleUserid, setScheduleUserid] = useState("");
  const [scheduleStatus, setScheduleStatus] = useState("");
  const [schedulePage, setSchedulePage] = useState(1);
  const [schedulesPagination, setSchedulesPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });

  // --- Load Handlers ---
  const loadUsers = async (
    pageVal = userPage,
    emailVal = userEmail,
    useridVal = userUserid,
    statusVal = userStatus,
    roleVal = userRole
  ) => {
    setUsersLoading(true);
    try {
      const { data } = await apiGet("/api/admin/users", {
        page: pageVal,
        limit: 20,
        email: emailVal || undefined,
        userid: useridVal || undefined,
        status: statusVal || undefined,
        role: roleVal || undefined,
      });
      const list = Array.isArray(data?.data) ? data.data : [];
      setUsersRows(list);
      const nextDrafts = {};
      for (const row of list) {
        nextDrafts[row.id] = { status: row.status, role: row.role };
      }
      setUserDrafts(nextDrafts);
      setUsersPagination(data?.pagination || { page: pageVal, totalPages: 1, total: 0, limit: 20 });
      setUserPage(pageVal);
    } catch (err) {
      showError(parseApiError(err, "Failed to load users"));
    } finally {
      setUsersLoading(false);
    }
  };

  const loadProjects = async (
    pageVal = projectPage,
    emailVal = projectEmail,
    useridVal = projectUserid,
    urlVal = projectUrl
  ) => {
    setProjectsLoading(true);
    try {
      const { data } = await apiGet("/api/admin/projects", {
        page: pageVal,
        limit: 20,
        email: emailVal || undefined,
        userid: useridVal || undefined,
        url: urlVal || undefined,
      });
      setProjectsRows(Array.isArray(data?.data) ? data.data : []);
      setProjectsPagination(data?.pagination || { page: pageVal, totalPages: 1, total: 0, limit: 20 });
      setProjectPage(pageVal);
    } catch (err) {
      showError(parseApiError(err, "Failed to load projects"));
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadSchedules = async (
    pageVal = schedulePage,
    emailVal = scheduleEmail,
    useridVal = scheduleUserid,
    statusVal = scheduleStatus
  ) => {
    setSchedulesLoading(true);
    try {
      const { data } = await apiGet("/api/schedules", {
        page: pageVal,
        limit: 20,
        email: emailVal || undefined,
        userid: useridVal || undefined,
        status: statusVal || undefined,
      });
      setSchedulesRows(Array.isArray(data?.data) ? data.data : []);
      setSchedulesPagination(data?.pagination || { page: pageVal, totalPages: 1, total: 0, limit: 20 });
      setSchedulePage(pageVal);
    } catch (err) {
      showError(parseApiError(err, "Failed to load schedules"));
    } finally {
      setSchedulesLoading(false);
    }
  };

  // --- Trigger load on activeTab switch ---
  useEffect(() => {
    if (activeTab === "users") {
      loadUsers(1, "", "", "", "");
      setUserEmail("");
      setUserUserid("");
      setUserStatus("");
      setUserRole("");
    } else if (activeTab === "projects") {
      loadProjects(1, "", "", "");
      setProjectEmail("");
      setProjectUserid("");
      setProjectUrl("");
    } else if (activeTab === "schedules") {
      loadSchedules(1, "", "", "");
      setScheduleEmail("");
      setScheduleUserid("");
      setScheduleStatus("");
    }
  }, [activeTab]);

  // --- Filter Applications ---
  const applyUsersFilters = () => {
    const trimmedEmail = normalizeEmail(userEmail);
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      showError("Please enter a valid email for filtering.");
      return;
    }
    loadUsers(1, trimmedEmail, userUserid.trim(), userStatus, userRole);
  };

  const applyProjectsFilters = () => {
    const trimmedEmail = normalizeEmail(projectEmail);
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      showError("Please enter a valid email for filtering.");
      return;
    }
    loadProjects(1, trimmedEmail, projectUserid.trim(), projectUrl.trim());
  };

  const applySchedulesFilters = () => {
    const trimmedEmail = normalizeEmail(scheduleEmail);
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      showError("Please enter a valid email for filtering.");
      return;
    }
    loadSchedules(1, trimmedEmail, scheduleUserid.trim(), scheduleStatus);
  };

  // --- User Save Handler ---
  const saveUser = async (row) => {
    const draft = userDrafts[row.id];
    if (!draft) return;
    const changedStatus = draft.status !== row.status;
    const changedRole = draft.role !== row.role;
    if (!changedStatus && !changedRole) return;
    setSavingUserId(row.id);
    try {
      await apiPatch(`/api/admin/users/${row.id}`, {
        status: draft.status,
        role: draft.role,
      });
      setUsersRows((current) =>
        current.map((item) =>
          item.id === row.id ? { ...item, status: draft.status, role: draft.role } : item
        )
      );
      showSuccess("User updated.");
    } catch (err) {
      showError(parseApiError(err, "Failed to update user"));
    } finally {
      setSavingUserId("");
    }
  };

  // --- Add Project Handler for Admin ---
  const handleAddProjectAdmin = async (e) => {
    e.preventDefault();
    if (!newProjEmail.trim() || !newProjUrl.trim()) return;
    if (!isValidEmail(normalizeEmail(newProjEmail))) {
      showError("Please enter a valid user email.");
      return;
    }
    setAddingProject(true);
    try {
      const { data } = await apiPost("/api/admin/projects", {
        email: normalizeEmail(newProjEmail),
        url: newProjUrl.trim(),
        label: newProjLabel.trim(),
      });
      showSuccess(`Project ${data.url} created successfully for ${newProjEmail}.`);
      setNewProjEmail("");
      setNewProjUrl("");
      setNewProjLabel("");
      loadProjects(1, "", "", "");
    } catch (err) {
      showError(parseApiError(err, "Failed to create project"));
    } finally {
      setAddingProject(false);
    }
  };

  // --- Project Delete Handler ---
  const deleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project? This will also remove any saved crawl data for this site.")) {
      return;
    }
    try {
      await apiDelete(`/api/admin/projects/${projectId}`);
      setProjectsRows((current) => current.filter((item) => item.id !== projectId));
      showSuccess("Project deleted successfully.");
    } catch (err) {
      showError(parseApiError(err, "Failed to delete project"));
    }
  };

  // --- Schedule Toggle & Delete Handlers ---
  const toggleSchedule = async (schedule) => {
    try {
      const nextEnabled = !schedule.enabled;
      await apiPatch(`/api/schedules/${schedule.id}`, {
        enabled: nextEnabled,
      });
      setSchedulesRows((current) =>
        current.map((item) =>
          item.id === schedule.id ? { ...item, enabled: nextEnabled } : item
        )
      );
      showSuccess(`Schedule status set to ${nextEnabled ? "Active" : "Paused"}.`);
    } catch (err) {
      showError(parseApiError(err, "Failed to update schedule status"));
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this monitoring schedule?")) {
      return;
    }
    try {
      await apiDelete(`/api/schedules/${scheduleId}`);
      setSchedulesRows((current) => current.filter((item) => item.id !== scheduleId));
      showSuccess("Schedule deleted successfully.");
    } catch (err) {
      showError(parseApiError(err, "Failed to delete schedule"));
    }
  };

  return (
    <div className="page page-admin-wide">
      <h1 className="page-title">Admin Dashboard</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px", marginBottom: "24px" }}>
        Global administrative portal to manage users, sites/projects, and configured schedules.
      </p>

      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem" }}>{sectionTitle}</h2>
          <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: "0.95rem" }}>
            Viewing the {sectionTitle.toLowerCase()} section of the admin portal.
          </p>
        </div>
        {/* compact=true: no "Backend health" label row, just dot + Live + time + refresh */}
        <HealthStatus compact />
      </div>

      {/* --- TAB 1: USERS --- */}
      {activeTab === "users" && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="input-group" style={{ marginBottom: 0 }}>
              <input
                className="input"
                type="text"
                placeholder="Filter by email..."
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
              />
              <input
                className="input"
                type="text"
                placeholder="Filter by user ID..."
                value={userUserid}
                onChange={(e) => setUserUserid(e.target.value)}
              />
              <select
                className="input"
                value={userStatus}
                onChange={(e) => setUserStatus(e.target.value)}
                style={{ maxWidth: 180 }}
              >
                {STATUS_OPTIONS.map((item) => (
                  <option key={item.value || "all-status"} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <select
                className="input"
                value={userRole}
                onChange={(e) => setUserRole(e.target.value)}
                style={{ maxWidth: 160 }}
              >
                {ROLE_OPTIONS.map((item) => (
                  <option key={item.value || "all-role"} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <button type="button" className="btn btn-primary" onClick={applyUsersFilters} disabled={usersLoading}>
                {usersLoading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>

          <div className="card">
            {usersLoading ? <div className="loading">Loading users...</div> : null}
            {!usersLoading && usersRows.length === 0 ? (
              <div className="empty-state">No users found.</div>
            ) : null}

            {!usersLoading && usersRows.length > 0 ? (
              <>
                <div className="table-wrap admin-table-wrap">
                  <table className="table admin-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>User ID</th>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.email}</td>
                          <td>{row.userid || "—"}</td>
                          <td>{row.name || "—"}</td>
                          <td>
                            <select
                              className="input"
                              value={userDrafts[row.id]?.role || row.role}
                              onChange={(e) =>
                                setUserDrafts((prev) => ({
                                  ...prev,
                                  [row.id]: { ...(prev[row.id] || {}), role: e.target.value },
                                }))
                              }
                              style={{ minWidth: 130 }}
                              disabled={savingUserId === row.id}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td>
                            <select
                              className="input"
                              value={userDrafts[row.id]?.status || row.status}
                              onChange={(e) =>
                                setUserDrafts((prev) => ({
                                  ...prev,
                                  [row.id]: { ...(prev[row.id] || {}), status: e.target.value },
                                }))
                              }
                              style={{ minWidth: 150 }}
                              disabled={savingUserId === row.id}
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="INACTIVE">Inactive</option>
                              <option value="SUSPENDED">Suspended</option>
                            </select>
                          </td>
                          <td>{formatDateOnly(row.created_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={
                                savingUserId === row.id ||
                                ((userDrafts[row.id]?.status || row.status) === row.status &&
                                  (userDrafts[row.id]?.role || row.role) === row.role)
                              }
                              onClick={() => saveUser(row)}
                            >
                              {savingUserId === row.id ? "Saving..." : "Save"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={userPage <= 1 || usersLoading}
                    onClick={() => loadUsers(userPage - 1)}
                  >
                    {"<- Prev"}
                  </button>
                  <span className="pagination-info">
                    Page {usersPagination.page} of {usersPagination.totalPages} — {usersPagination.total} total
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={userPage >= usersPagination.totalPages || usersLoading}
                    onClick={() => loadUsers(userPage + 1)}
                  >
                    {"Next ->"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </>
      )}

      {/* --- TAB 2: PROJECTS --- */}
      {activeTab === "projects" && (
        <>
          {/* Add Project Form */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 className="section-title" style={{ marginTop: 0, marginBottom: 12, fontSize: "0.95rem", fontWeight: "700", textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>Add Project for User</h3>
            <form onSubmit={handleAddProjectAdmin} style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: 1, minWidth: "220px", display: "flex", flexDirection: "column" }}>
                <span className="filter-label" style={{ marginBottom: 6 }}>User Email</span>
                <input
                  type="email"
                  className="input"
                  placeholder="user@example.com"
                  value={newProjEmail}
                  onChange={(e) => setNewProjEmail(e.target.value)}
                  required
                />
              </div>
              <div style={{ flex: 1, minWidth: "220px", display: "flex", flexDirection: "column" }}>
                <span className="filter-label" style={{ marginBottom: 6 }}>Project URL</span>
                <input
                  type="text"
                  className="input"
                  placeholder="https://example.com"
                  value={newProjUrl}
                  onChange={(e) => setNewProjUrl(e.target.value)}
                  required
                />
              </div>
              <div style={{ flex: 1, minWidth: "180px", display: "flex", flexDirection: "column" }}>
                <span className="filter-label" style={{ marginBottom: 6 }}>Label (optional)</span>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. Acme Site"
                  value={newProjLabel}
                  onChange={(e) => setNewProjLabel(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ height: "42px" }} disabled={addingProject}>
                {addingProject ? "Adding..." : "Add Project"}
              </button>
            </form>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="input-group" style={{ marginBottom: 0 }}>
              <input
                className="input"
                type="text"
                placeholder="Filter by owner email..."
                value={projectEmail}
                onChange={(e) => setProjectEmail(e.target.value)}
              />
              <input
                className="input"
                type="text"
                placeholder="Filter by owner user ID..."
                value={projectUserid}
                onChange={(e) => setProjectUserid(e.target.value)}
              />
              <input
                className="input"
                type="text"
                placeholder="Filter by project URL..."
                value={projectUrl}
                onChange={(e) => setProjectUrl(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyProjectsFilters}
                disabled={projectsLoading}
              >
                {projectsLoading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>

          <div className="card">
            {projectsLoading ? <div className="loading">Loading projects...</div> : null}
            {!projectsLoading && projectsRows.length === 0 ? (
              <div className="empty-state">No projects found.</div>
            ) : null}

            {!projectsLoading && projectsRows.length > 0 ? (
              <>
                <div className="table-wrap admin-table-wrap">
                  <table className="table admin-table">
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Label</th>
                        <th>Owner Email</th>
                        <th>Owner User ID</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectsRows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <a href={row.url} target="_blank" rel="noreferrer" className="url-text">
                              {row.url}
                            </a>
                          </td>
                          <td>{row.label || "—"}</td>
                          <td>{row.user_email || "—"}</td>
                          <td>{row.user_userid || "—"}</td>
                          <td>{formatDateOnly(row.created_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm text-danger"
                              onClick={() => deleteProject(row.id)}
                              style={{ color: "var(--danger)" }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={projectPage <= 1 || projectsLoading}
                    onClick={() => loadProjects(projectPage - 1)}
                  >
                    {"<- Prev"}
                  </button>
                  <span className="pagination-info">
                    Page {projectsPagination.page} of {projectsPagination.totalPages} — {projectsPagination.total} total
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={projectPage >= projectsPagination.totalPages || projectsLoading}
                    onClick={() => loadProjects(projectPage + 1)}
                  >
                    {"Next ->"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </>
      )}

      {/* --- TAB 3: SCHEDULES --- */}
      {activeTab === "schedules" && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="input-group" style={{ marginBottom: 0 }}>
              <input
                className="input"
                type="text"
                placeholder="Filter by owner email..."
                value={scheduleEmail}
                onChange={(e) => setScheduleEmail(e.target.value)}
              />
              <input
                className="input"
                type="text"
                placeholder="Filter by owner user ID..."
                value={scheduleUserid}
                onChange={(e) => setScheduleUserid(e.target.value)}
              />
              <select
                className="input"
                value={scheduleStatus}
                onChange={(e) => setScheduleStatus(e.target.value)}
                style={{ maxWidth: 180 }}
              >
                <option value="">All statuses</option>
                <option value="active">Active Only</option>
                <option value="paused">Paused Only</option>
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applySchedulesFilters}
                disabled={schedulesLoading}
              >
                {schedulesLoading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>

          <div className="card">
            {schedulesLoading ? <div className="loading">Loading schedules...</div> : null}
            {!schedulesLoading && schedulesRows.length === 0 ? (
              <div className="empty-state">No schedules found.</div>
            ) : null}

            {!schedulesLoading && schedulesRows.length > 0 ? (
              <>
                <div className="table-wrap admin-table-wrap">
                  <table className="table admin-table">
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Label</th>
                        <th>Frequency</th>
                        <th>Owner Email</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedulesRows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <a href={row.url} target="_blank" rel="noreferrer" className="url-text">
                              {row.url}
                            </a>
                          </td>
                          <td>{row.label || "—"}</td>
                          <td style={{ textTransform: "capitalize" }}>{row.frequency}</td>
                          <td>{row.user_email || "—"}</td>
                          <td>
                            <button
                              type="button"
                              className={`btn btn-sm ${row.enabled ? "btn-success" : "btn-ghost"}`}
                              onClick={() => toggleSchedule(row)}
                              style={{
                                padding: "4px 10px",
                                fontSize: "0.8rem",
                                background: row.enabled ? "#dcfce7" : "#f1f5f9",
                                color: row.enabled ? "#15803d" : "#475569",
                                border: "1px solid " + (row.enabled ? "#bbf7d0" : "#cbd5e1"),
                                cursor: "pointer",
                              }}
                            >
                              {row.enabled ? "Active" : "Paused"}
                            </button>
                          </td>
                          <td>{formatDateOnly(row.created_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm text-danger"
                              onClick={() => deleteSchedule(row.id)}
                              style={{ color: "var(--danger)" }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={schedulePage <= 1 || schedulesLoading}
                    onClick={() => loadSchedules(schedulePage - 1)}
                  >
                    {"<- Prev"}
                  </button>
                  <span className="pagination-info">
                    Page {schedulesPagination.page} of {schedulesPagination.totalPages} — {schedulesPagination.total} total
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={schedulePage >= schedulesPagination.totalPages || schedulesLoading}
                    onClick={() => loadSchedules(schedulePage + 1)}
                  >
                    {"Next ->"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </>
      )}

      {/* --- TAB 4: SUBSCRIBERS --- */}
      {activeTab === "subscribers" && (
        <AdminSubscribers inline={true} />
      )}

      {/* --- TAB 5: METRICS --- */}
      {activeTab === "metrics" && (
        <Metrics inline={true} />
      )}

    </div>
  );
}